import * as plugins from './plugins.js';

type TVirtualStreamDirection =
  plugins.typedRequestInterfaces.TVirtualStreamDirection;
type TVirtualStreamEndpoint =
  plugins.typedRequestInterfaces.TVirtualStreamTransportEndpoint<TVirtualStreamDirection>;
type TVirtualStreamDescriptor =
  plugins.typedRequestInterfaces.IVirtualStreamDescriptor<TVirtualStreamDirection>;

const maximumVirtualStreamOpenTimeoutMs = 10_000;
const applicationAuthorizedStreams = new WeakSet<VirtualStream>();
const activationScopeOwners = new WeakMap<
  VirtualStream,
  VirtualStreamActivationScope
>();

const authorizeVirtualStreamApplicationUse = (streamArg: VirtualStream): void => {
  applicationAuthorizedStreams.add(streamArg);
};

interface IEncodedPayloadState {
  standard: WeakMap<object, any>;
  omittingResponseRequestStreams: WeakMap<object, any>;
  containsNetworkDescriptor?: boolean;
  requiresNetworkDescriptorScan?: boolean;
}

interface IVirtualStreamEncodingResult {
  readonly payload: any;
  readonly containsNetworkDescriptor: boolean;
}

export interface ICommFunctions {
  side: 'requesting' | 'responding';
  activationScope?: VirtualStreamActivationScope;
  virtualStreamTransport?: plugins.typedRequestInterfaces.IVirtualStreamTransport;
  openDeadlineAt?: number;
  abortSignal?: AbortSignal;
}

export interface IVirtualStreamRegistrationOptions<
  TDirection extends TVirtualStreamDirection,
> {
  readonly transport: plugins.typedRequestInterfaces.IVirtualStreamTransport;
  readonly registration:
    plugins.typedRequestInterfaces.IVirtualStreamTransportRegistration<TDirection>;
}

export class VirtualStreamClosedError extends Error {
  constructor(messageArg = 'VirtualStream is closed', optionsArg?: ErrorOptions) {
    super(messageArg, optionsArg);
    this.name = 'VirtualStreamClosedError';
  }
}

export class VirtualStreamOpenTimeoutError extends Error {
  constructor(
    public readonly timeoutMs: number,
    public readonly deadlineAt: number,
  ) {
    super(
      timeoutMs > 0
        ? `VirtualStream transport staging timed out after ${timeoutMs}ms`
        : 'VirtualStream transport staging deadline is exhausted',
    );
    this.name = 'VirtualStreamOpenTimeoutError';
  }
}

export class VirtualStreamLifecycleContractError extends Error {
  constructor(terminalKindArg: 'accept' | 'close') {
    super(
      `VirtualStream endpoint closed before terminal ${terminalKindArg} produced a receipt`,
    );
    this.name = 'VirtualStreamLifecycleContractError';
  }
}

/** Tracks stream activation during one request/response handshake. */
export class VirtualStreamActivationScope {
  private trackedStreams = new Set<VirtualStream>();
  private hasAuthorizedTrackedStreams = false;
  private published = false;
  private settled = false;

  public track(streamArg: VirtualStream): void {
    if (this.settled) {
      throw new Error('Cannot track a VirtualStream in a settled activation scope');
    }
    const existingOwner = activationScopeOwners.get(streamArg);
    if (existingOwner && existingOwner !== this) {
      throw new Error('VirtualStream is already owned by another activation scope');
    }
    if (!existingOwner) activationScopeOwners.set(streamArg, this);
    this.trackedStreams.add(streamArg);
    if (this.hasAuthorizedTrackedStreams) {
      authorizeVirtualStreamApplicationUse(streamArg);
    }
    if (this.published) streamArg.publishActivation();
  }

  public authorizeApplicationUse(): void {
    if (this.settled || this.hasAuthorizedTrackedStreams) return;
    this.hasAuthorizedTrackedStreams = true;
    for (const stream of this.trackedStreams) {
      authorizeVirtualStreamApplicationUse(stream);
    }
  }

  public publish(): void {
    if (this.settled || this.published) return;
    this.published = true;
    for (const stream of this.trackedStreams) stream.publishActivation();
  }

  public commit(): void {
    this.publish();
    this.settled = true;
    this.trackedStreams.clear();
  }

  public async rollback(reasonArg?: unknown): Promise<void> {
    if (this.settled) return;
    this.settled = true;
    const streams = [...this.trackedStreams];
    this.trackedStreams.clear();
    await Promise.allSettled(
      streams.map((streamArg) => streamArg.rollbackActivation(reasonArg)),
    );
  }
}

type TVirtualStreamTerminalKind = 'abort' | 'accept' | 'close' | 'reject';

/** A direction-specific facade over one exact virtual-stream-v1 endpoint. */
export class VirtualStream {
  private static readonly maximumStreamIdLength = 256;

  public static fromRegistration<
    TDirection extends TVirtualStreamDirection,
  >(
    optionsArg: IVirtualStreamRegistrationOptions<TDirection>,
  ): plugins.typedRequestInterfaces.TVirtualStream<TDirection> {
    let dispose: (() => void) | undefined = optionsArg.registration?.dispose;
    const disposeOnce = () => {
      const disposeNow = dispose;
      dispose = undefined;
      disposeNow?.();
    };

    try {
      VirtualStream.validateTransport(optionsArg.transport);
      if (typeof optionsArg.registration?.dispose !== 'function') {
        throw new TypeError('VirtualStream registration requires a synchronous disposer');
      }
      VirtualStream.validateNetworkDescriptor(optionsArg.registration.descriptor);
      VirtualStream.validateEndpoint(
        optionsArg.registration.endpoint,
        optionsArg.registration.descriptor,
        optionsArg.registration.descriptor.creatorDirection,
      );
      return new VirtualStream({
        transport: optionsArg.transport,
        descriptor: optionsArg.registration.descriptor,
        endpoint: optionsArg.registration.endpoint,
        registrationDispose: disposeOnce,
        explicitRegistration: true,
      }) as unknown as plugins.typedRequestInterfaces.TVirtualStream<TDirection>;
    } catch (error) {
      disposeOnce();
      throw error;
    }
  }

  public static encodePayloadForNetwork(
    objectPayloadArg: any,
    commFunctionsArg: ICommFunctions,
  ): any {
    const isResponseEnvelope =
      objectPayloadArg?.correlation?.phase === 'response';
    return VirtualStream.encodePayloadValue(
      objectPayloadArg,
      commFunctionsArg,
      isResponseEnvelope,
      false,
      true,
      {
        standard: new WeakMap<object, any>(),
        omittingResponseRequestStreams: new WeakMap<object, any>(),
      },
    );
  }

  /** @internal */
  public static encodePayloadForNetworkWithMetadata(
    objectPayloadArg: any,
    commFunctionsArg: ICommFunctions,
  ): IVirtualStreamEncodingResult {
    const isResponseEnvelope =
      objectPayloadArg?.correlation?.phase === 'response';
    const encodedPayloadState: IEncodedPayloadState = {
      standard: new WeakMap<object, any>(),
      omittingResponseRequestStreams: new WeakMap<object, any>(),
      containsNetworkDescriptor: false,
      requiresNetworkDescriptorScan: false,
    };
    const payload = VirtualStream.encodePayloadValue(
      objectPayloadArg,
      commFunctionsArg,
      isResponseEnvelope,
      false,
      true,
      encodedPayloadState,
    );
    const requiresNetworkDescriptorScan =
      encodedPayloadState.requiresNetworkDescriptorScan
      || VirtualStream.encodedContainerPrototypesMayExposeNetworkDescriptor();
    return {
      payload,
      containsNetworkDescriptor: requiresNetworkDescriptorScan
        ? VirtualStream.containsNetworkDescriptor(payload)
        : encodedPayloadState.containsNetworkDescriptor!,
    };
  }

  private static encodePayloadValue(
    objectPayloadArg: any,
    commFunctionsArg: ICommFunctions,
    isResponseEnvelopeArg: boolean,
    omitResponseRequestStreamsArg: boolean,
    isPayloadRootArg: boolean,
    encodedPayloadStateArg: IEncodedPayloadState,
  ): any {
    if (!objectPayloadArg) return objectPayloadArg;
    if (plugins.smartbuffer.isBufferLike(objectPayloadArg)) return objectPayloadArg;
    if (objectPayloadArg instanceof VirtualStream) {
      if (omitResponseRequestStreamsArg) {
        return {
          _OMITTED_VIRTUAL_STREAM: true,
          reason: 'Streams beneath an echoed response.request are omitted.',
        };
      }
      if (encodedPayloadStateArg.containsNetworkDescriptor !== undefined) {
        encodedPayloadStateArg.containsNetworkDescriptor = true;
      }
      return objectPayloadArg.activateForEncoding(commFunctionsArg);
    }
    const isPassThroughValue =
      objectPayloadArg instanceof Set
      || objectPayloadArg instanceof Map
      || objectPayloadArg instanceof Date
      || objectPayloadArg instanceof RegExp
      || objectPayloadArg instanceof Error
      || objectPayloadArg instanceof Promise
      || typeof objectPayloadArg.then === 'function';
    if (isPassThroughValue) {
      // Preserve post-encode marker access order for identity-preserved objects.
      if (
        encodedPayloadStateArg.containsNetworkDescriptor !== undefined
        && typeof objectPayloadArg === 'object'
      ) {
        encodedPayloadStateArg.requiresNetworkDescriptorScan = true;
      }
      return objectPayloadArg;
    }
    if (typeof objectPayloadArg !== 'object') return objectPayloadArg;

    const encodedObjects = omitResponseRequestStreamsArg
      ? encodedPayloadStateArg.omittingResponseRequestStreams
      : encodedPayloadStateArg.standard;
    const existingEncodedObject = encodedObjects.get(objectPayloadArg);
    if (existingEncodedObject) return existingEncodedObject;
    if (Array.isArray(objectPayloadArg)) {
      const encodedArray: any[] = [];
      encodedObjects.set(objectPayloadArg, encodedArray);
      for (let index = 0; index < objectPayloadArg.length; index++) {
        encodedArray.push(VirtualStream.encodePayloadValue(
          objectPayloadArg[index],
          commFunctionsArg,
          isResponseEnvelopeArg,
          omitResponseRequestStreamsArg,
          false,
          encodedPayloadStateArg,
        ));
      }
      if (
        encodedPayloadStateArg.containsNetworkDescriptor !== undefined
        && Object.getPrototypeOf(encodedArray) !== Array.prototype
      ) {
        encodedPayloadStateArg.requiresNetworkDescriptorScan = true;
      }
      return encodedArray;
    }

    const encodedObject: Record<string, any> = {};
    encodedObjects.set(objectPayloadArg, encodedObject);
    for (const key of Object.keys(objectPayloadArg)) {
      const omitNestedResponseRequestStreams =
        omitResponseRequestStreamsArg
        || (isResponseEnvelopeArg && isPayloadRootArg && key === 'request');
      encodedObject[key] = VirtualStream.encodePayloadValue(
        objectPayloadArg[key],
        commFunctionsArg,
        isResponseEnvelopeArg,
        omitNestedResponseRequestStreams,
        false,
        encodedPayloadStateArg,
      );
    }
    if (
      encodedPayloadStateArg.containsNetworkDescriptor !== undefined
      && Object.getPrototypeOf(encodedObject) !== Object.prototype
    ) {
      encodedPayloadStateArg.requiresNetworkDescriptorScan = true;
    }
    if (
      encodedPayloadStateArg.containsNetworkDescriptor !== undefined
      && Object.hasOwn(encodedObject, '_isVirtualStream')
      && encodedObject._isVirtualStream === true
    ) {
      encodedPayloadStateArg.containsNetworkDescriptor = true;
    }
    return encodedObject;
  }

  public static async decodePayloadFromNetworkAsync(
    objectPayloadArg: any,
    commFunctionsArg: ICommFunctions,
  ): Promise<any> {
    const decodedStreams = new Map<string, {
      stream: VirtualStream;
      descriptor: TVirtualStreamDescriptor;
    }>();
    const streamsToOpen: Array<{
      stream: VirtualStream;
      descriptor: TVirtualStreamDescriptor;
    }> = [];
    const createdStreams: VirtualStream[] = [];
    const activationScope =
      commFunctionsArg.activationScope ?? new VirtualStreamActivationScope();
    const ownsActivationScope = !commFunctionsArg.activationScope;
    const openDeadlineAt = VirtualStream.resolveOpenDeadlineAt(
      commFunctionsArg.openDeadlineAt,
    );

    try {
      const decodedPayload = VirtualStream.preparePayloadFromNetwork(
        objectPayloadArg,
        { ...commFunctionsArg, activationScope },
        decodedStreams,
        streamsToOpen,
        createdStreams,
        new WeakMap<object, any>(),
      );

      if (streamsToOpen.length > 0) {
        const transport = commFunctionsArg.virtualStreamTransport;
        if (!transport) {
          throw new Error(
            'VirtualStream descriptor requires a virtual stream transport',
          );
        }
        VirtualStream.validateTransport(transport);
        for (const streamToOpen of streamsToOpen) {
          const openTimeoutMs = VirtualStream.getRemainingOpenTimeoutMs(
            openDeadlineAt,
          );
          const endpoint = await VirtualStream.openVirtualStreamWithTimeout(
            transport,
            streamToOpen.descriptor,
            openTimeoutMs,
            openDeadlineAt,
            commFunctionsArg.abortSignal,
          );
          streamToOpen.stream.bindOpenedEndpoint(endpoint);
        }
      }

      if (ownsActivationScope) {
        activationScope.authorizeApplicationUse();
        activationScope.commit();
      }
      return decodedPayload;
    } catch (error) {
      await Promise.allSettled(
        createdStreams.map((streamArg) => streamArg.rollbackActivation(error)),
      );
      throw error;
    }
  }

  public static containsNetworkDescriptor(
    objectPayloadArg: any,
    checkedObjectsArg = new WeakSet<object>(),
  ): boolean {
    if (
      !objectPayloadArg
      || plugins.smartbuffer.isBufferLike(objectPayloadArg)
      || typeof objectPayloadArg !== 'object'
    ) {
      return false;
    }
    if (objectPayloadArg._isVirtualStream === true) return true;
    if (checkedObjectsArg.has(objectPayloadArg)) return false;
    checkedObjectsArg.add(objectPayloadArg);
    if (Array.isArray(objectPayloadArg)) {
      return objectPayloadArg.some((itemArg) =>
        VirtualStream.containsNetworkDescriptor(itemArg, checkedObjectsArg)
      );
    }
    if (
      objectPayloadArg instanceof Set
      || objectPayloadArg instanceof Map
      || objectPayloadArg instanceof Date
      || objectPayloadArg instanceof RegExp
      || objectPayloadArg instanceof Error
      || objectPayloadArg instanceof Promise
      || typeof objectPayloadArg.then === 'function'
    ) {
      return false;
    }
    return Object.values(objectPayloadArg).some((valueArg) =>
      VirtualStream.containsNetworkDescriptor(valueArg, checkedObjectsArg)
    );
  }

  private static encodedContainerPrototypesMayExposeNetworkDescriptor(): boolean {
    return Object.getPrototypeOf(Object.prototype) !== null
      || Object.getPrototypeOf(Array.prototype) !== Object.prototype
      || Object.getOwnPropertyDescriptor(
        Object.prototype,
        '_isVirtualStream',
      ) !== undefined
      || Object.getOwnPropertyDescriptor(
        Array.prototype,
        '_isVirtualStream',
      ) !== undefined;
  }

  private static preparePayloadFromNetwork(
    objectPayloadArg: any,
    commFunctionsArg: ICommFunctions,
    decodedStreamsArg: Map<string, {
      stream: VirtualStream;
      descriptor: TVirtualStreamDescriptor;
    }>,
    streamsToOpenArg: Array<{
      stream: VirtualStream;
      descriptor: TVirtualStreamDescriptor;
    }>,
    createdStreamsArg: VirtualStream[],
    decodedObjectsArg: WeakMap<object, any>,
  ): any {
    if (
      plugins.smartbuffer.isBufferLike(objectPayloadArg)
      || objectPayloadArg === null
      || typeof objectPayloadArg !== 'object'
    ) {
      return objectPayloadArg;
    }
    if (
      objectPayloadArg instanceof Set
      || objectPayloadArg instanceof Map
      || objectPayloadArg instanceof Date
      || objectPayloadArg instanceof RegExp
      || objectPayloadArg instanceof Error
      || objectPayloadArg instanceof Promise
      || typeof objectPayloadArg.then === 'function'
    ) {
      return objectPayloadArg;
    }
    if (objectPayloadArg._isVirtualStream === true) {
      VirtualStream.validateNetworkDescriptor(objectPayloadArg);
      const descriptor = objectPayloadArg;
      const decodedStream = decodedStreamsArg.get(descriptor.streamId);
      if (decodedStream) {
        if (!VirtualStream.descriptorMetadataMatches(
          decodedStream.descriptor,
          descriptor,
        )) {
          throw new TypeError(
            `Conflicting VirtualStream metadata for streamId "${descriptor.streamId}"`,
          );
        }
        return decodedStream.stream;
      }

      const transport = commFunctionsArg.virtualStreamTransport;
      const virtualStream = new VirtualStream({
        transport,
        descriptor,
        explicitRegistration: false,
      });
      decodedStreamsArg.set(descriptor.streamId, {
        stream: virtualStream,
        descriptor,
      });
      streamsToOpenArg.push({ stream: virtualStream, descriptor });
      createdStreamsArg.push(virtualStream);
      commFunctionsArg.activationScope?.track(virtualStream);
      return virtualStream;
    }

    const existingDecodedObject = decodedObjectsArg.get(objectPayloadArg);
    if (existingDecodedObject) return existingDecodedObject;
    if (Array.isArray(objectPayloadArg)) {
      const decodedArray: any[] = [];
      decodedObjectsArg.set(objectPayloadArg, decodedArray);
      for (const item of objectPayloadArg) {
        decodedArray.push(VirtualStream.preparePayloadFromNetwork(
          item,
          commFunctionsArg,
          decodedStreamsArg,
          streamsToOpenArg,
          createdStreamsArg,
          decodedObjectsArg,
        ));
      }
      return decodedArray;
    }

    const decodedObject: Record<string, any> = {};
    decodedObjectsArg.set(objectPayloadArg, decodedObject);
    for (const key of Object.keys(objectPayloadArg)) {
      Object.defineProperty(decodedObject, key, {
        value: VirtualStream.preparePayloadFromNetwork(
          objectPayloadArg[key],
          commFunctionsArg,
          decodedStreamsArg,
          streamsToOpenArg,
          createdStreamsArg,
          decodedObjectsArg,
        ),
        configurable: true,
        enumerable: true,
        writable: true,
      });
    }
    return decodedObject;
  }

  private static resolveOpenDeadlineAt(deadlineAtArg?: number): number {
    const packageDeadlineAt = performance.now() + maximumVirtualStreamOpenTimeoutMs;
    if (deadlineAtArg === undefined) return packageDeadlineAt;
    if (!Number.isFinite(deadlineAtArg) || deadlineAtArg < 0) {
      throw new RangeError(
        'VirtualStream openDeadlineAt must be a non-negative finite number',
      );
    }
    return Math.min(deadlineAtArg, packageDeadlineAt);
  }

  private static getRemainingOpenTimeoutMs(deadlineAtArg: number): number {
    const timeoutMs = Math.ceil(deadlineAtArg - performance.now());
    if (!Number.isSafeInteger(timeoutMs) || timeoutMs <= 0) {
      throw new VirtualStreamOpenTimeoutError(0, deadlineAtArg);
    }
    return timeoutMs;
  }

  private static async openVirtualStreamWithTimeout(
    transportArg: plugins.typedRequestInterfaces.IVirtualStreamTransport,
    descriptorArg: TVirtualStreamDescriptor,
    timeoutMsArg: number,
    deadlineAtArg: number,
    parentAbortSignalArg?: AbortSignal,
  ): Promise<TVirtualStreamEndpoint> {
    const timeoutError = new VirtualStreamOpenTimeoutError(
      timeoutMsArg,
      deadlineAtArg,
    );
    const abortController = new AbortController();
    let terminalReason: unknown;
    let timeout: ReturnType<typeof setTimeout> | undefined;
    let parentAbortListener: (() => void) | undefined;
    const openPromise = Promise.resolve().then(
      () => transportArg.openVirtualStream(descriptorArg, {
        abortSignal: abortController.signal,
        timeoutMs: timeoutMsArg,
      }) as Promise<TVirtualStreamEndpoint>,
    );

    void openPromise.then(
      async (endpointArg) => {
        if (terminalReason !== undefined) {
          await VirtualStream.abortLateOpenedEndpoint(endpointArg, terminalReason);
        }
      },
      () => {},
    );

    const interruptionPromise = new Promise<never>((_resolve, reject) => {
      timeout = setTimeout(() => {
        terminalReason = timeoutError;
        abortController.abort(timeoutError);
        reject(timeoutError);
      }, timeoutMsArg);
      if (parentAbortSignalArg) {
        parentAbortListener = () => {
          const reason = parentAbortSignalArg.reason
            ?? new DOMException('The operation was aborted', 'AbortError');
          terminalReason = reason;
          abortController.abort(reason);
          reject(reason);
        };
        parentAbortSignalArg.addEventListener('abort', parentAbortListener, { once: true });
        if (parentAbortSignalArg.aborted) parentAbortListener();
      }
    });
    try {
      return await Promise.race([openPromise, interruptionPromise]);
    } finally {
      if (timeout) clearTimeout(timeout);
      if (parentAbortSignalArg && parentAbortListener) {
        parentAbortSignalArg.removeEventListener('abort', parentAbortListener);
      }
    }
  }

  private static async abortLateOpenedEndpoint(
    endpointArg: TVirtualStreamEndpoint,
    reasonArg: unknown,
  ): Promise<void> {
    let abortPromise: Promise<void> | undefined;
    try {
      abortPromise = Promise.resolve(endpointArg.abort(reasonArg));
    } catch {}
    void abortPromise?.catch(() => {});
    try {
      await endpointArg.closed;
    } catch {}
  }

  private static validateTransport(
    transportArg: plugins.typedRequestInterfaces.IVirtualStreamTransport,
  ): void {
    if (
      !transportArg
      || typeof transportArg !== 'object'
      || typeof transportArg.openVirtualStream !== 'function'
    ) {
      throw new TypeError('VirtualStream transport is invalid');
    }
  }

  private static validateNetworkDescriptor(
    descriptorArg: unknown,
  ): asserts descriptorArg is TVirtualStreamDescriptor {
    if (!VirtualStream.isPlainObject(descriptorArg)) {
      throw new TypeError('VirtualStream descriptor must be a plain object');
    }
    const marker = VirtualStream.getOwnDataProperty(descriptorArg, '_isVirtualStream');
    const protocol = VirtualStream.getOwnDataProperty(descriptorArg, 'protocol');
    const streamId = VirtualStream.getOwnDataProperty(descriptorArg, 'streamId');
    const creatorDirection = VirtualStream.getOwnDataProperty(
      descriptorArg,
      'creatorDirection',
    );
    if (marker !== true || protocol !== 'virtual-stream-v1') {
      throw new TypeError('VirtualStream descriptor requires protocol virtual-stream-v1');
    }
    if (
      typeof streamId !== 'string'
      || streamId.length === 0
      || streamId.length > VirtualStream.maximumStreamIdLength
    ) {
      throw new TypeError(
        `VirtualStream descriptor streamId must contain 1-${VirtualStream.maximumStreamIdLength} characters`,
      );
    }
    if (creatorDirection !== 'send' && creatorDirection !== 'receive') {
      throw new TypeError(
        'VirtualStream descriptor creatorDirection must be send or receive',
      );
    }

    const contentTypeProperty = Object.getOwnPropertyDescriptor(
      descriptorArg,
      'contentType',
    );
    if (contentTypeProperty) {
      if (
        !('value' in contentTypeProperty)
        || typeof contentTypeProperty.value !== 'string'
        || contentTypeProperty.value.length === 0
      ) {
        throw new TypeError(
          'VirtualStream descriptor contentType must be a non-empty string',
        );
      }
    }

    const integrityProperty = Object.getOwnPropertyDescriptor(
      descriptorArg,
      'integrity',
    );
    if (integrityProperty) {
      if (!('value' in integrityProperty)) {
        throw new TypeError('VirtualStream descriptor integrity must be data');
      }
      VirtualStream.validateIntegrity(integrityProperty.value);
    }

    const transportProperty = Object.getOwnPropertyDescriptor(
      descriptorArg,
      'transport',
    );
    if (!transportProperty || !('value' in transportProperty)) {
      throw new TypeError(
        'VirtualStream descriptor requires an own transport property',
      );
    }
    if (!VirtualStream.isJsonCompatible(transportProperty.value, new WeakSet())) {
      throw new TypeError(
        'VirtualStream descriptor transport must be JSON-compatible',
      );
    }
  }

  private static validateIntegrity(integrityArg: unknown): void {
    if (!VirtualStream.isPlainObject(integrityArg)) {
      throw new TypeError('VirtualStream integrity must be a plain object');
    }
    const algorithm = VirtualStream.getOwnDataProperty(integrityArg, 'algorithm');
    const byteLength = VirtualStream.getOwnDataProperty(integrityArg, 'byteLength');
    const digest = VirtualStream.getOwnDataProperty(integrityArg, 'digest');
    if (algorithm !== 'sha256') {
      throw new TypeError('VirtualStream integrity algorithm must be sha256');
    }
    if (!Number.isSafeInteger(byteLength) || (byteLength as number) < 0) {
      throw new TypeError(
        'VirtualStream integrity byteLength must be a non-negative safe integer',
      );
    }
    if (
      typeof digest !== 'string'
      || !/^sha256:[0-9a-f]{64}$/.test(digest)
    ) {
      throw new TypeError('VirtualStream integrity digest is invalid');
    }
  }

  private static validateEndpoint(
    endpointArg: unknown,
    descriptorArg: TVirtualStreamDescriptor,
    expectedDirectionArg: TVirtualStreamDirection,
  ): asserts endpointArg is TVirtualStreamEndpoint {
    if (!endpointArg || typeof endpointArg !== 'object') {
      throw new TypeError('VirtualStream transport returned an invalid endpoint');
    }
    const endpoint = endpointArg as Partial<TVirtualStreamEndpoint>;
    if (
      endpoint.protocol !== 'virtual-stream-v1'
      || endpoint.direction !== expectedDirectionArg
      || endpoint.streamId !== descriptorArg.streamId
      || endpoint.contentType !== descriptorArg.contentType
      || !VirtualStream.integrityMatches(endpoint.integrity, descriptorArg.integrity)
    ) {
      throw new TypeError(
        'VirtualStream endpoint direction or metadata does not match its descriptor',
      );
    }
    if (
      !('opened' in endpoint)
      || !('completion' in endpoint)
      || !endpoint.closed
      || typeof endpoint.closed.then !== 'function'
      || typeof endpoint.abort !== 'function'
    ) {
      throw new TypeError('VirtualStream endpoint lacks lifecycle contracts');
    }
    if (expectedDirectionArg === 'send') {
      if (
        typeof (endpoint as Partial<plugins.typedRequestInterfaces.IVirtualStreamSender>).send
          !== 'function'
        || typeof (endpoint as Partial<plugins.typedRequestInterfaces.IVirtualStreamSender>).close
          !== 'function'
        || !('writable' in endpoint)
      ) {
        throw new TypeError('VirtualStream sender endpoint is invalid');
      }
    } else if (
      typeof (endpoint as Partial<plugins.typedRequestInterfaces.IVirtualStreamReceiver>).receive
        !== 'function'
      || typeof (endpoint as Partial<plugins.typedRequestInterfaces.IVirtualStreamReceiver>).accept
        !== 'function'
      || typeof (endpoint as Partial<plugins.typedRequestInterfaces.IVirtualStreamReceiver>).reject
        !== 'function'
      || !('readable' in endpoint)
    ) {
      throw new TypeError('VirtualStream receiver endpoint is invalid');
    }
  }

  private static descriptorMetadataMatches(
    firstArg: TVirtualStreamDescriptor,
    secondArg: TVirtualStreamDescriptor,
  ): boolean {
    return firstArg.creatorDirection === secondArg.creatorDirection
      && firstArg.contentType === secondArg.contentType
      && VirtualStream.integrityMatches(firstArg.integrity, secondArg.integrity);
  }

  private static integrityMatches(
    firstArg: plugins.typedRequestInterfaces.IVirtualStreamIntegrity | undefined,
    secondArg: plugins.typedRequestInterfaces.IVirtualStreamIntegrity | undefined,
  ): boolean {
    if (!firstArg || !secondArg) return firstArg === secondArg;
    return firstArg.algorithm === secondArg.algorithm
      && firstArg.byteLength === secondArg.byteLength
      && firstArg.digest === secondArg.digest;
  }

  private static getOwnDataProperty(
    objectArg: object,
    keyArg: string,
  ): unknown {
    const property = Object.getOwnPropertyDescriptor(objectArg, keyArg);
    if (!property || !('value' in property)) return undefined;
    return property.value;
  }

  private static isPlainObject(valueArg: unknown): valueArg is Record<string, unknown> {
    if (!valueArg || typeof valueArg !== 'object' || Array.isArray(valueArg)) {
      return false;
    }
    const prototype = Object.getPrototypeOf(valueArg);
    return prototype === Object.prototype || prototype === null;
  }

  private static isJsonCompatible(
    valueArg: unknown,
    ancestorsArg: WeakSet<object>,
  ): boolean {
    if (
      valueArg === null
      || typeof valueArg === 'string'
      || typeof valueArg === 'boolean'
    ) {
      return true;
    }
    if (typeof valueArg === 'number') return Number.isFinite(valueArg);
    if (!valueArg || typeof valueArg !== 'object') return false;
    if (ancestorsArg.has(valueArg)) return false;
    ancestorsArg.add(valueArg);
    try {
      if (Array.isArray(valueArg)) {
        for (let index = 0; index < valueArg.length; index++) {
          const itemProperty = Object.getOwnPropertyDescriptor(valueArg, String(index));
          if (
            !itemProperty
            || !('value' in itemProperty)
            || !VirtualStream.isJsonCompatible(itemProperty.value, ancestorsArg)
          ) {
            return false;
          }
        }
        return Reflect.ownKeys(valueArg).every((keyArg) =>
          keyArg === 'length'
          || (typeof keyArg === 'string'
            && /^(0|[1-9][0-9]*)$/.test(keyArg)
            && Number(keyArg) < valueArg.length)
        );
      }
      if (!VirtualStream.isPlainObject(valueArg)) return false;
      for (const key of Reflect.ownKeys(valueArg)) {
        if (typeof key !== 'string') return false;
        const property = Object.getOwnPropertyDescriptor(valueArg, key)!;
        if (
          !property.enumerable
          || !('value' in property)
          || !VirtualStream.isJsonCompatible(property.value, ancestorsArg)
        ) {
          return false;
        }
      }
      return true;
    } finally {
      ancestorsArg.delete(valueArg);
    }
  }

  private readonly transport:
    | plugins.typedRequestInterfaces.IVirtualStreamTransport
    | undefined;
  private readonly descriptor: TVirtualStreamDescriptor;
  private endpoint: TVirtualStreamEndpoint | undefined;
  private registrationDispose: (() => void) | undefined;
  private readonly explicitRegistration: boolean;
  private activationPublished = false;
  private endpointUseStarted = false;
  private endpointClosed = false;
  private locallyDisposed = false;
  private closeError: VirtualStreamClosedError | undefined;
  private terminalPromise: Promise<unknown> | undefined;
  private terminalKind: TVirtualStreamTerminalKind | undefined;
  private completionOutcome:
    | {
        status: 'fulfilled';
        value: plugins.typedRequestInterfaces.IVirtualStreamReceipt;
      }
    | { status: 'rejected'; error: unknown }
    | undefined;

  private constructor(optionsArg: {
    transport?: plugins.typedRequestInterfaces.IVirtualStreamTransport;
    descriptor: TVirtualStreamDescriptor;
    endpoint?: TVirtualStreamEndpoint;
    registrationDispose?: () => void;
    explicitRegistration: boolean;
  }) {
    this.transport = optionsArg.transport;
    this.descriptor = optionsArg.descriptor;
    this.endpoint = optionsArg.endpoint;
    this.registrationDispose = optionsArg.registrationDispose;
    this.explicitRegistration = optionsArg.explicitRegistration;
    if (this.explicitRegistration) authorizeVirtualStreamApplicationUse(this);
    if (this.endpoint) this.observeEndpoint(this.endpoint);
  }

  public get protocol(): 'virtual-stream-v1' {
    return 'virtual-stream-v1';
  }

  public get direction(): TVirtualStreamDirection {
    if (this.endpoint) return this.endpoint.direction;
    return this.descriptor.creatorDirection === 'send' ? 'receive' : 'send';
  }

  public get streamId(): string {
    return this.descriptor.streamId;
  }

  public get contentType(): string | undefined {
    return this.descriptor.contentType;
  }

  public get integrity():
    | plugins.typedRequestInterfaces.IVirtualStreamIntegrity
    | undefined {
    return this.descriptor.integrity;
  }

  public get opened(): Promise<void> {
    try {
      this.assertApplicationOperationsAllowed();
      this.assertNotClosed();
      this.endpointUseStarted = true;
      const opened = this.requireEndpoint().opened;
      if (!opened || typeof opened.then !== 'function') {
        throw new TypeError('VirtualStream endpoint opened contract is invalid');
      }
      return opened;
    } catch (error) {
      return Promise.reject(error);
    }
  }

  public get completion(): Promise<plugins.typedRequestInterfaces.IVirtualStreamReceipt> {
    const completion = this.requireEndpoint().completion;
    if (!completion || typeof completion.then !== 'function') {
      throw new TypeError('VirtualStream endpoint completion contract is invalid');
    }
    return completion;
  }

  public get closed(): Promise<void> {
    return this.requireEndpoint().closed;
  }

  public get writable(): WritableStream<Uint8Array> {
    this.assertApplicationOperation('send');
    this.endpointUseStarted = true;
    return (
      this.requireEndpoint() as plugins.typedRequestInterfaces.IVirtualStreamSender
    ).writable;
  }

  public get readable(): ReadableStream<Uint8Array> {
    this.assertApplicationOperation('receive');
    this.endpointUseStarted = true;
    return (
      this.requireEndpoint() as plugins.typedRequestInterfaces.IVirtualStreamReceiver
    ).readable;
  }

  public publishActivation(): void {
    if (this.explicitRegistration && !this.locallyDisposed) {
      this.activationPublished = true;
    }
  }

  public async rollbackActivation(reasonArg?: unknown): Promise<void> {
    if (this.locallyDisposed) return;
    if (
      this.explicitRegistration
      && !this.activationPublished
      && !this.endpointUseStarted
    ) {
      this.disposeUnpublishedRegistration();
      return;
    }
    if (!this.endpoint) return;
    await this.abort(reasonArg);
  }

  public async send(chunkArg: Uint8Array): Promise<void> {
    this.assertApplicationOperation('send');
    if (!(chunkArg instanceof Uint8Array)) {
      throw new TypeError('VirtualStream.send accepts only Uint8Array chunks');
    }
    this.endpointUseStarted = true;
    await (
      this.requireEndpoint() as plugins.typedRequestInterfaces.IVirtualStreamSender
    ).send(chunkArg);
  }

  public async receive(): Promise<Uint8Array | undefined> {
    this.assertApplicationOperation('receive');
    this.endpointUseStarted = true;
    return (
      this.requireEndpoint() as plugins.typedRequestInterfaces.IVirtualStreamReceiver
    ).receive();
  }

  public close(): Promise<plugins.typedRequestInterfaces.IVirtualStreamReceipt> {
    if (this.terminalPromise) {
      return this.followTerminal<plugins.typedRequestInterfaces.IVirtualStreamReceipt>(
        'close',
      );
    }
    try {
      this.assertApplicationOperation('send');
      this.endpointUseStarted = true;
      const endpoint =
        this.requireEndpoint() as plugins.typedRequestInterfaces.IVirtualStreamSender;
      return this.terminateEndpoint(() => endpoint.close(), 'close');
    } catch (error) {
      return Promise.reject(error);
    }
  }

  public accept(): Promise<plugins.typedRequestInterfaces.IVirtualStreamReceipt> {
    if (this.terminalPromise) {
      return this.followTerminal<plugins.typedRequestInterfaces.IVirtualStreamReceipt>(
        'accept',
      );
    }
    try {
      this.assertApplicationOperation('receive');
      this.endpointUseStarted = true;
      const endpoint =
        this.requireEndpoint() as plugins.typedRequestInterfaces.IVirtualStreamReceiver;
      return this.terminateEndpoint(() => endpoint.accept(), 'accept');
    } catch (error) {
      return Promise.reject(error);
    }
  }

  public reject(reasonArg?: unknown): Promise<void> {
    if (this.terminalPromise) return this.followTerminal<void>('reject');
    try {
      this.assertApplicationOperation('receive');
      this.endpointUseStarted = true;
      const endpoint =
        this.requireEndpoint() as plugins.typedRequestInterfaces.IVirtualStreamReceiver;
      return this.terminateEndpoint(() => endpoint.reject(reasonArg), 'reject');
    } catch (error) {
      return Promise.reject(error);
    }
  }

  public abort(reasonArg?: unknown): Promise<void> {
    if (this.terminalPromise) return this.followTerminal<void>('abort');
    if (this.locallyDisposed || this.endpointClosed) return Promise.resolve();
    const endpoint = this.endpoint;
    if (!endpoint) return Promise.resolve();
    this.endpointUseStarted = true;
    return this.terminateEndpoint(() => endpoint.abort(reasonArg), 'abort');
  }

  private activateForEncoding(commFunctionsArg: ICommFunctions): TVirtualStreamDescriptor {
    commFunctionsArg.activationScope?.track(this);
    try {
      if (!commFunctionsArg.virtualStreamTransport) {
        throw new Error('VirtualStream requires a virtual stream transport');
      }
      if (this.transport !== commFunctionsArg.virtualStreamTransport) {
        throw new Error(
          'VirtualStream cannot be encoded through a different virtual stream transport',
        );
      }
      this.assertNotClosed();
      if (this.terminalPromise) {
        throw new VirtualStreamClosedError('VirtualStream has a terminal operation');
      }
      if (!commFunctionsArg.activationScope) this.publishActivation();
      return this.descriptor;
    } catch (error) {
      if (!commFunctionsArg.activationScope) {
        void this.rollbackActivation(error).catch(() => {});
      }
      throw error;
    }
  }

  private bindOpenedEndpoint(endpointArg: TVirtualStreamEndpoint): void {
    this.endpoint = endpointArg;
    const expectedDirection = this.descriptor.creatorDirection === 'send'
      ? 'receive'
      : 'send';
    VirtualStream.validateEndpoint(
      endpointArg,
      this.descriptor,
      expectedDirection,
    );
    this.observeEndpoint(endpointArg);
  }

  private observeEndpoint(endpointArg: TVirtualStreamEndpoint): void {
    const completion = endpointArg.completion;
    if (!completion || typeof completion.then !== 'function') {
      throw new TypeError('VirtualStream endpoint completion contract is invalid');
    }
    void completion.then(
      (valueArg) => {
        this.completionOutcome = { status: 'fulfilled', value: valueArg };
      },
      (errorArg) => {
        this.completionOutcome = { status: 'rejected', error: errorArg };
      },
    );
    void endpointArg.closed.then(
      () => this.markEndpointClosed(),
      (errorArg) => this.markEndpointClosed(errorArg),
    );
  }

  private markEndpointClosed(reasonArg?: unknown): void {
    if (reasonArg !== undefined) this.getCloseError(reasonArg);
    this.endpointClosed = true;
    this.disposeRegistration();
  }

  private disposeRegistration(): void {
    const dispose = this.registrationDispose;
    this.registrationDispose = undefined;
    dispose?.();
  }

  private disposeUnpublishedRegistration(): void {
    if (this.locallyDisposed) return;
    this.locallyDisposed = true;
    this.disposeRegistration();
  }

  private requireEndpoint(): TVirtualStreamEndpoint {
    if (!this.endpoint) throw new Error('VirtualStream endpoint is not staged');
    return this.endpoint;
  }

  private assertApplicationOperationsAllowed(): void {
    if (!applicationAuthorizedStreams.has(this)) {
      throw new Error('VirtualStream application use is not authorized');
    }
  }

  private assertApplicationOperation(
    expectedDirectionArg: TVirtualStreamDirection,
  ): void {
    this.assertApplicationOperationsAllowed();
    this.assertNotClosed();
    if (this.terminalPromise) {
      throw new VirtualStreamClosedError('VirtualStream has a terminal operation');
    }
    if (this.direction !== expectedDirectionArg) {
      throw new TypeError(
        `${this.direction} VirtualStreams cannot perform ${expectedDirectionArg} operations`,
      );
    }
  }

  private assertNotClosed(): void {
    if (this.endpointClosed || this.locallyDisposed) throw this.getCloseError();
  }

  private getCloseError(reasonArg?: unknown): VirtualStreamClosedError {
    if (!this.closeError) {
      this.closeError = new VirtualStreamClosedError(
        'VirtualStream is closed',
        reasonArg === undefined ? undefined : { cause: reasonArg },
      );
    }
    return this.closeError;
  }

  private followTerminal<TResult>(
    kindArg: TVirtualStreamTerminalKind,
  ): Promise<TResult> {
    const terminalPromise = this.terminalPromise!;
    if (this.terminalKind === kindArg) return terminalPromise as Promise<TResult>;
    const conflictError = new VirtualStreamClosedError(
      `VirtualStream terminal ${this.terminalKind} already completed`,
    );
    return terminalPromise.then(
      () => Promise.reject(conflictError),
      () => Promise.reject(conflictError),
    );
  }

  private terminateEndpoint<TResult>(
    operationArg: () => Promise<TResult>,
    kindArg: TVirtualStreamTerminalKind,
  ): Promise<TResult> {
    if (this.terminalPromise) return this.followTerminal<TResult>(kindArg);
    const endpoint = this.requireEndpoint();
    this.terminalKind = kindArg;
    let operation: (() => Promise<TResult>) | undefined = operationArg;
    const operationPromise = Promise.resolve().then(() => {
      const currentOperation = operation!;
      operation = undefined;
      return currentOperation();
    });
    void operationPromise.catch(() => {});
    const operationOutcome = operationPromise.then(
      (valueArg) => ({ status: 'fulfilled' as const, value: valueArg }),
      (errorArg) => ({ status: 'rejected' as const, error: errorArg }),
    );
    let observedOperationOutcome:
      | Awaited<typeof operationOutcome>
      | undefined;
    void operationOutcome.then((outcomeArg) => {
      observedOperationOutcome = outcomeArg;
    });
    const closedOutcome = endpoint.closed.then(
      () => ({ status: 'closed' as const }),
      (errorArg) => ({ status: 'closedError' as const, error: errorArg }),
    );
    const terminalPromise = (async () => {
      const firstOutcome = await Promise.race([operationOutcome, closedOutcome]);
      if (
        firstOutcome.status === 'fulfilled'
        || firstOutcome.status === 'rejected'
      ) {
        const finalClosedOutcome = await closedOutcome;
        this.endpointClosed = true;
        this.disposeRegistration();
        if (firstOutcome.status === 'rejected') throw firstOutcome.error;
        if (finalClosedOutcome.status === 'closedError') {
          throw finalClosedOutcome.error;
        }
        return firstOutcome.value;
      }

      this.endpointClosed = true;
      this.disposeRegistration();
      if (firstOutcome.status === 'closedError') throw firstOutcome.error;
      await Promise.resolve();
      await Promise.resolve();
      if (observedOperationOutcome?.status === 'rejected') {
        throw observedOperationOutcome.error;
      }
      if (observedOperationOutcome?.status === 'fulfilled') {
        return observedOperationOutcome.value;
      }
      if (kindArg === 'close' || kindArg === 'accept') {
        if (this.completionOutcome?.status === 'fulfilled') {
          return this.completionOutcome.value as TResult;
        }
        if (this.completionOutcome?.status === 'rejected') {
          throw this.completionOutcome.error;
        }
        throw new VirtualStreamLifecycleContractError(kindArg);
      }
      return undefined as TResult;
    })();
    this.terminalPromise = terminalPromise;
    return terminalPromise;
  }
}
